import { useState, useEffect } from 'react';
import {
  getAttendanceConfig, adminEmployeeOverrides, adminSetEmployeeAttendance, adminEmployeesWithPin,
  adminSetEmployeePin, adminResetDevices, describeOverride, REMOTE_OVERRIDE,
} from '../lib/attendance';

// Métodos efectivos de una persona: configuración de la organización + su excepción
export const effectiveMethods = (cfg, o) => {
  if (!cfg) return [];
  const ov = o || {};
  const m = { ...cfg.methods, ...(ov.methods || {}) };
  const out = [];
  if (m.phone) {
    const extra = [];
    if (cfg.phone.require_location && !ov.skip_location) extra.push('GPS');
    if (cfg.phone.require_biometric && !ov.skip_biometric) extra.push('huella');
    out.push('Celular' + (extra.length ? ' · ' + extra.join(' + ') : ''));
  }
  if (m.qr_screen) out.push('QR en pantalla');
  if (m.kiosk) out.push('Kiosco');
  return out;
};
export const methodsLine = (cfg, o) => {
  const l = effectiveMethods(cfg, o);
  const base = l.length ? l.map((x) => x.split(' · ')[0]).join(' · ') : 'Solo manual';
  return o && Object.keys(o).length ? `${base} · excepción` : base;
};

const Toggle = ({ on, onChange, label, disabled }) => (
  <label className={`flex items-center justify-between gap-3 py-1.5 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
    <span className="text-sm text-gray-700">{label}</span>
    <button type="button" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)}
      className={`shrink-0 w-10 h-6 rounded-full transition-colors relative ${on ? 'bg-sky-600' : 'bg-gray-200'}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
    </button>
  </label>
);

export default function EmployeeMethods({ emp, canEdit, showToast, onChanged }) {
  const [cfg, setCfg] = useState(null);
  const [o, setO] = useState({});
  const [saved, setSaved] = useState({});
  const [hasPin, setHasPin] = useState(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const c = await getAttendanceConfig();
        const ov = (await adminEmployeeOverrides())[emp.id] || {};
        if (!alive) return;
        setCfg(c); setO(JSON.parse(JSON.stringify(ov))); setSaved(ov);
        if (canEdit) { try { const w = await adminEmployeesWithPin(); if (alive) setHasPin(w.includes(emp.id)); } catch (e) { /* sin permiso */ } }
      } catch (e) { showToast?.(e.message || 'No se pudo cargar', 'error'); }
    })();
    return () => { alive = false; };
  }, [emp.id, canEdit]);

  if (!cfg) return <p className="text-sm text-gray-400 text-center py-10">Cargando…</p>;

  const dirty = JSON.stringify(o) !== JSON.stringify(saved);
  const orgM = cfg.methods;
  const val = (k) => (o.methods && k in o.methods ? o.methods[k] : orgM[k]);
  const setM = (k, v) => setO((x) => ({ ...x, methods: { ...(x.methods || {}), [k]: v } }));
  const eff = effectiveMethods(cfg, o);

  const save = async () => {
    setBusy(true);
    try { await adminSetEmployeeAttendance(emp.id, o); setSaved(JSON.parse(JSON.stringify(o))); showToast?.('Guardado'); onChanged?.(); }
    catch (e) { showToast?.(e.message || 'No se pudo guardar', 'error'); }
    setBusy(false);
  };
  const genPin = async () => {
    try { const p = await adminSetEmployeePin(emp.id); setPin(p); setHasPin(true); }
    catch (e) { showToast?.(e.message || 'No se pudo generar el código', 'error'); }
  };
  const resetDev = async () => {
    if (!window.confirm('¿Restablecer los dispositivos de confianza de ' + emp.name + '? El próximo celular desde el que marque quedará como habitual.')) return;
    try { await adminResetDevices(emp.id); showToast?.('Dispositivos restablecidos'); }
    catch (e) { showToast?.(e.message || 'No se pudo', 'error'); }
  };

  const Sec = ({ title, children }) => (
    <div className="rounded-2xl border border-gray-100 p-3.5"><p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">{title}</p>{children}</div>
  );

  return (
    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
      <Sec title="Cómo marca hoy">
        {eff.length ? (
          <div className="flex flex-wrap gap-1.5">{eff.map((x) => <span key={x} className="px-2.5 py-1 rounded-lg bg-sky-50 text-sky-700 text-xs font-bold">{x}</span>)}</div>
        ) : <p className="text-sm text-gray-500">Sin métodos propios activos: solo registro manual del administrador.</p>}
        <p className="text-xs text-gray-400 mt-2">
          {Object.keys(saved).length ? `Excepción propia: ${describeOverride(saved)}.` : 'Hereda lo que eligió la organización.'}
          {cfg.network.restrict && !o.skip_network ? ' Solo desde la red del local.' : ''}
        </p>
      </Sec>

      <Sec title="Excepción de esta persona">
        {canEdit && (
          <div className="flex flex-wrap gap-2 mb-2">
            <button type="button" onClick={() => setO({})} className="text-xs px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 font-semibold text-gray-600">Como la organización</button>
            <button type="button" onClick={() => setO(JSON.parse(JSON.stringify(REMOTE_OVERRIDE)))} className="text-xs px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 font-semibold text-gray-600">Trabaja remoto</button>
          </div>
        )}
        <div className="divide-y divide-gray-50">
          <Toggle disabled={!canEdit} on={!!val('phone')} onChange={(v) => setM('phone', v)} label="Celular" />
          <Toggle disabled={!canEdit} on={!!val('qr_screen')} onChange={(v) => setM('qr_screen', v)} label="QR en pantalla" />
          <Toggle disabled={!canEdit} on={!!val('kiosk')} onChange={(v) => setM('kiosk', v)} label="Kiosco" />
          <Toggle disabled={!canEdit} on={!!o.skip_network} onChange={(v) => setO((x) => ({ ...x, skip_network: v }))} label="Sin restricción de red" />
          <Toggle disabled={!canEdit} on={!!o.skip_location} onChange={(v) => setO((x) => ({ ...x, skip_location: v }))} label="Sin control de ubicación" />
          <Toggle disabled={!canEdit} on={!!o.skip_biometric} onChange={(v) => setO((x) => ({ ...x, skip_biometric: v }))} label="Sin huella / Face ID" />
        </div>
        {canEdit && dirty && <button type="button" onClick={save} disabled={busy} className="mt-3 w-full text-sm font-semibold text-white bg-sky-600 py-2.5 rounded-xl disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar'}</button>}
      </Sec>

      <Sec title="Huella y dispositivos">
        <p className="text-sm text-gray-700">{emp.bio_registered ? 'Huella / Face ID registrada.' : 'Todavía no registró huella / Face ID.'}</p>
        {canEdit && <button type="button" onClick={resetDev} className="mt-2 text-xs font-bold text-sky-700">Restablecer dispositivos de confianza</button>}
      </Sec>

      {canEdit && hasPin !== null && (
        <Sec title="Código de kiosco">
          <p className="text-sm text-gray-700">{pin ? <>Código nuevo: <span className="font-mono font-bold tracking-widest text-base">{pin}</span> <span className="text-xs text-gray-400">(anotalo, no se vuelve a mostrar)</span></> : hasPin ? 'Tiene un código asignado.' : 'No tiene código.'}</p>
          <button type="button" onClick={genPin} className="mt-2 text-xs font-bold text-sky-700">{hasPin ? 'Generar uno nuevo' : 'Generar código'}</button>
        </Sec>
      )}
    </div>
  );
}
